/**
 * RF-12 · Informe de autoevaluación (`/api/informes`).
 *
 * Redacción por componente con editor enriquecido y generación de PDF. El estado
 * `avalado` es el que la regla de alerta A5 cruza con la fecha de radicación del
 * calendario.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http, primerError, getAuthToken } from '@/services/http'

// RF-12 · Informe de autoevaluación de una ejecución.

export interface SeccionInforme {
  clave: string
  titulo: string
  clase: 'estatica' | 'automatica' | 'redactada' | 'comparativa' | 'libre'
  visible: boolean
  contenido?: string | null
}

/** Forma con la que el API de informe devuelve este dato. */
export interface TipoInforme {
  id_tipo_informe: number
  nombre: string
  etiqueta: string
  estructura: 'A' | 'B'
  titulo_portada: string
  secciones_default: SeccionInforme[]
}

/** Forma con la que el API de informe devuelve este dato. */
export interface RedaccionInforme {
  id_redaccion: number
  id_informe: number
  id_componente: number
  bloque: string
  contenido: string | null
  redactado_por: number | null
  componente?: { id_componente: number; nombre: string; padre_id: number | null }
}

/** Forma con la que el API de informe devuelve este dato. */
export interface Informe {
  id_informe: number
  id_ejecucion: number
  id_tipo_informe: number
  titulo: string | null
  secciones: SeccionInforme[]
  presentacion: string | null
  descripcion_programa: string | null
  analisis_resultados: string | null
  conclusion: string | null
  estado: 'borrador' | 'en_revision' | 'avalado'
  avalado_en: string | null
  tipo?: TipoInforme
  redacciones?: RedaccionInforme[]
}

interface Resultado<T> {
  ok: boolean
  status: number
  data?: T
  error?: string
}

/**
 * Consulta informe · `GET /api/tipos-informe`.
 *
 * @returns Resultado<TipoInforme[]>
 */
export async function getTiposInformeService(): Promise<Resultado<TipoInforme[]>> {
  const r = await http.get<TipoInforme[]>('/api/tipos-informe')
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudieron cargar los tipos de informe') }
}

/**
 * Genera informe · `POST /api/ejecuciones`.
 *
 * @returns Resultado<Informe>
 */
export async function generarInformeService(idEjecucion: number, idTipoInforme: number): Promise<Resultado<Informe>> {
  const r = await http.post<Informe>(`/api/ejecuciones/${idEjecucion}/informe`, { id_tipo_informe: idTipoInforme })
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo generar el informe') }
}

/**
 * Consulta informe · `GET /api/ejecuciones`.
 *
 * @returns Resultado<Informe>
 */
export async function getInformeService(idEjecucion: number): Promise<Resultado<Informe>> {
  const r = await http.get<Informe>(`/api/ejecuciones/${idEjecucion}/informe`)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo cargar el informe') }
}

/**
 * Actualiza informe · `PATCH /api/informes`.
 *
 * @returns Resultado<Informe>
 */
export async function actualizarInformeService(idInforme: number, cambios: Partial<Informe>): Promise<Resultado<Informe>> {
  const r = await http.patch<Informe>(`/api/informes/${idInforme}`, cambios)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo actualizar el informe') }
}

/**
 * Guarda informe · petición `PUT`.
 *
 * @returns Resultado<RedaccionInforme>
 */
export async function guardarRedaccionService(
  idInforme: number,
  idComponente: number,
  bloque: string,
  contenido: string | null
): Promise<Resultado<RedaccionInforme>> {
  const r = await http.put<RedaccionInforme>(
    `/api/informes/${idInforme}/redacciones/${idComponente}/${bloque}`,
    { contenido }
  )
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo guardar la redacción') }
}

/**
 * Operación sobre informe · `POST /api/informes`.
 *
 * @returns Resultado<Informe>
 */
export async function avalarInformeService(idInforme: number): Promise<Resultado<Informe>> {
  const r = await http.post<Informe>(`/api/informes/${idInforme}/avalar`)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo avalar el informe') }
}

/** Descarga binaria del PDF (fetch directo con el token de sesión). */
export async function descargarPdfInformeService(idInforme: number): Promise<Resultado<null>> {
  const base = process.env.VUE_APP_API_BASE_URL ?? ''
  const token = getAuthToken()
  const res = await fetch(`${base}/api/informes/${idInforme}/pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  })
  if (!res.ok) {
    return { ok: false, status: res.status, error: 'No se pudo descargar el PDF del informe' }
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `informe-autoevaluacion-${idInforme}.pdf`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
  return { ok: true, status: res.status, data: null }
}
